import { BATCH_FUTURE_UID, TIME_UNIT, VIP_APPLY_BY } from "../constants";
import { OFFER_DIRECTION, TRIGGER_TYPE } from "./unifiedOffer";
import { asArray, normalizeClockToHHMM, normalizeClockTo12h } from "./tierOps";

/**
 * Maps the form's string enums to the backend integer contract
 * (scoot-backend `OfferDirection / OfferTriggerType / OfferSelectionMethod`)
 * and builds the top-level `trigger_config` each trigger shape expects.
 * Pure — unit-tested against the Postman collection examples.
 */

export const BACKEND_DIRECTION = {
  [OFFER_DIRECTION.DISCOUNT]: 1,
  [OFFER_DIRECTION.SURGE]: 2,
};

export const BACKEND_TRIGGER_TYPE = {
  [TRIGGER_TYPE.SCHEDULE]: 1,
  [TRIGGER_TYPE.EVERGREEN]: 2,
  [TRIGGER_TYPE.SLOTS_FILLED]: 3,
  [TRIGGER_TYPE.ENROLLMENT_FILLED]: 3,
  [TRIGGER_TYPE.SEATS_FILLED]: 3,
  [TRIGGER_TYPE.STOCK_SOLD]: 3,
  [TRIGGER_TYPE.SLOTS_LEFT]: 4,
  [TRIGGER_TYPE.SEATS_LEFT]: 4,
  [TRIGGER_TYPE.SPOTS_LEFT]: 4,
  [TRIGGER_TYPE.STOCK_LEFT]: 4,
  [TRIGGER_TYPE.TIME_TO_SLOT]: 5,
  [TRIGGER_TYPE.TIME_TO_EVENT]: 5,
  [TRIGGER_TYPE.TIME_TO_COHORT_START]: 5,
  [TRIGGER_TYPE.TIME_TO_BATCH_START]: 5,
  [TRIGGER_TYPE.MEMBER_COUNT]: 6,
  [TRIGGER_TYPE.VIP_SLOTS]: 7,
  [TRIGGER_TYPE.BATCH_WISE]: 8,
};

export const BACKEND_SELECTION_METHOD = {
  lowest: 1,
  highest: 2,
  recent: 3,
};

/** Backend int → form string (detail/list load path). */
export const SELECTION_FROM_BACKEND = {
  1: "lowest",
  2: "highest",
  3: "recent",
};

/** Backend direction int → form string. Exact (1:1). */
export const DIRECTION_FROM_BACKEND = {
  1: OFFER_DIRECTION.DISCOUNT,
  2: OFFER_DIRECTION.SURGE,
};

/**
 * Backend trigger int → form string. Fallback-only: ints 3/4/5 each cover
 * several family labels, so the stored metadata string (exact) always wins
 * when present and this is only for records created outside the form.
 * Int 5 resolves to TIME_TO_SLOT — the backend's own canonical label for 5
 * ("Time to Slot", confirmed in trigger/config responses) and the same pick
 * the checkout apps make, so all three surfaces agree.
 */
export const TRIGGER_FROM_BACKEND = {
  1: TRIGGER_TYPE.SCHEDULE,
  2: TRIGGER_TYPE.EVERGREEN,
  3: TRIGGER_TYPE.SEATS_FILLED,
  4: TRIGGER_TYPE.SEATS_LEFT,
  5: TRIGGER_TYPE.TIME_TO_SLOT,
  6: TRIGGER_TYPE.MEMBER_COUNT,
  7: TRIGGER_TYPE.VIP_SLOTS,
  8: TRIGGER_TYPE.BATCH_WISE,
};

/** Monday-first, matching backend `interval must be a weekday 0 (Mon) to 6 (Sun)`. */
export const WEEKDAY_INDEX = {
  Monday: 0,
  Tuesday: 1,
  Wednesday: 2,
  Thursday: 3,
  Friday: 4,
  Saturday: 5,
  Sunday: 6,
};

export const TIME_TRIGGER_INTS = [1, 2];

export const mapDirection = (direction) =>
  BACKEND_DIRECTION[direction] || BACKEND_DIRECTION[OFFER_DIRECTION.DISCOUNT];

export const mapTriggerType = (trigger) =>
  BACKEND_TRIGGER_TYPE[trigger] || BACKEND_TRIGGER_TYPE[TRIGGER_TYPE.SCHEDULE];

export const mapSelectionMethod = (method) =>
  BACKEND_SELECTION_METHOD[method] || BACKEND_SELECTION_METHOD.lowest;

export const mapSelectionMethodFromBackend = (method) =>
  SELECTION_FROM_BACKEND[method] || SELECTION_FROM_BACKEND[1];

export const mapDirectionFromBackend = (direction) =>
  DIRECTION_FROM_BACKEND[direction] || null;

export const mapTriggerFromBackend = (trigger) =>
  TRIGGER_FROM_BACKEND[trigger] || null;

/**
 * Stored backend `trigger_config` → form builder rows. Used on edit when the
 * offer was created outside this form (or metadata was dropped): tiers,
 * vip windows (grouped per day/date) and batch rows (+ future default).
 * Returns empty arrays when the config carries nothing usable.
 */
export const rowsFromTriggerConfig = (config = {}) => {
  const empty = { tiers: [], vip_windows: [], batch_values: [] };
  if (!config || typeof config !== "object") return empty;

  if (Array.isArray(config.tiers) && config.tiers.length) {
    const timeLike = config.tiers.some((t) => t?.before !== undefined);
    return {
      ...empty,
      tiers: config.tiers.map((t) =>
        timeLike
          ? {
              threshold: t?.before ?? "",
              threshold_unit: t?.before_unit || TIME_UNIT.DAYS,
              value: t?.value ?? "",
              value_international: t?.value_international,
            }
          : {
              threshold: t?.threshold ?? "",
              value: t?.value ?? "",
              value_international: t?.value_international,
            }
      ),
    };
  }

  if (Array.isArray(config.schedules) && config.schedules.length) {
    const byInterval = new Map();
    config.schedules.forEach((s) => {
      const key = String(s?.interval ?? "");
      if (!byInterval.has(key)) {
        const idx = Number(s?.interval);
        byInterval.set(key, {
          label:
            Number.isInteger(idx) && TRIGGER_WEEKDAYS[idx]
              ? TRIGGER_WEEKDAYS[idx]
              : key,
          ranges: [],
          markup: s?.value ?? "",
        });
      }
      const row = byInterval.get(key);
      row.ranges.push({
        start: normalizeClockTo12h(s?.time_range?.[0]) || "",
        end: normalizeClockTo12h(s?.time_range?.[1]) || "",
      });
      if (row.markup === "" || row.markup === undefined)
        row.markup = s?.value ?? "";
      if (row.markup_international === undefined)
        row.markup_international = s?.value_international;
    });
    return { ...empty, vip_windows: [...byInterval.values()] };
  }

  if (Array.isArray(config.rows) || config.default) {
    const batch_values = (config.rows || []).map((r) => ({
      batch_uid: r?.batch_uuid || "",
      value: r?.value ?? "",
      value_international: r?.value_international,
    }));
    if (config.default) {
      batch_values.push({
        batch_uid: BATCH_FUTURE_UID,
        label: "All future batches (default)",
        value: config.default?.value ?? "",
        value_international: config.default?.value_international,
      });
    }
    return { ...empty, batch_values };
  }

  return empty;
};

/** Weekday index → form row label (backend 0 = Monday). */
export const TRIGGER_WEEKDAYS = [
  "Monday",
  "Tuesday",
  "Wednesday",
  "Thursday",
  "Friday",
  "Saturday",
  "Sunday",
];

/**
 * Backend ints win; legacy metadata strings are the fallback.
 * List rows carry both once the new backend ships.
 */
export const isSurgeRecord = (record = {}) => {
  if (record?.direction === BACKEND_DIRECTION[OFFER_DIRECTION.SURGE])
    return true;
  if (record?.direction === BACKEND_DIRECTION[OFFER_DIRECTION.DISCOUNT])
    return false;
  return record?.metadata?.direction === OFFER_DIRECTION.SURGE;
};

export const directionLabel = (record = {}) =>
  isSurgeRecord(record) ? "Surge" : "Early Bird";

const numOrNull = (value) => {
  if (value === "" || value === null || value === undefined) return null;
  const num = Number(value);
  return Number.isNaN(num) ? null : num;
};

/** Backend ints handled by the count-tier builder (seats/slots/stock/members). */
export const COUNT_TIER_TRIGGER_INTS = [3, 4, 6];
/** Backend int for the time-proximity (days/hours before) tier builder. */
export const TIME_TIER_TRIGGER_INTS = [5];

export const isCountTierTrigger = (trigger) =>
  COUNT_TIER_TRIGGER_INTS.includes(mapTriggerType(trigger));
export const isTimeTierTrigger = (trigger) =>
  TIME_TIER_TRIGGER_INTS.includes(mapTriggerType(trigger));

const buildCountTiers = ({ tiers, threshold_unit, value_unit }) => {
  const cleaned = asArray(tiers)
    .map((tier) => {
      const intl = numOrNull(tier?.value_international);
      return {
        threshold: numOrNull(tier?.threshold),
        value: numOrNull(tier?.value),
        ...(intl !== null && { value_international: intl }),
      };
    })
    .filter((tier) => tier.threshold !== null && tier.value !== null);
  if (!cleaned.length) return null;
  return { threshold_unit, value_unit, tiers: cleaned };
};

/** Exported for the international (`value_international`) count-tier payload. */
export { buildCountTiers };

const buildTimeTiers = ({ tiers, value_unit }) => {
  const cleaned = asArray(tiers)
    .map((tier) => {
      const intl = numOrNull(tier?.value_international);
      return {
        before: numOrNull(tier?.threshold),
        before_unit: tier?.threshold_unit || TIME_UNIT.DAYS,
        value: numOrNull(tier?.value),
        ...(intl !== null && { value_international: intl }),
      };
    })
    .filter(
      (tier) => tier.before !== null && tier.value !== null && tier.before_unit
    );
  if (!cleaned.length) return null;
  return { value_unit, tiers: cleaned };
};

/** Exported for the international (`value_international`) time-tier payload. */
export { buildTimeTiers };

const buildVipSchedules = ({
  vip_apply_by,
  vip_windows,
  vip_start_date,
  vip_end_date,
  value_unit,
}) => {
  const weekly = vip_apply_by !== VIP_APPLY_BY.DATE;
  const schedules = [];
  asArray(vip_windows).forEach((row, idx) => {
    const interval = weekly
      ? WEEKDAY_INDEX[row?.label] ?? idx
      : (row?.label || "").trim();
    if (weekly && (interval < 0 || interval > 6)) return;
    if (!weekly && !interval) return;
    asArray(row?.ranges).forEach((range) => {
      const start = normalizeClockToHHMM(range?.start || "");
      const end = normalizeClockToHHMM(range?.end || "");
      const value = numOrNull(row?.markup);
      if (!start || !end || value === null) return;
      const intl = numOrNull(row?.markup_international);
      schedules.push({
        interval,
        time_range: [start, end],
        value,
        ...(intl !== null && { value_international: intl }),
      });
    });
  });
  if (!schedules.length) return null;
  // Backend sorts the same way; pre-sorting keeps update structure checks stable.
  schedules.sort((a, b) =>
    a.interval > b.interval
      ? 1
      : a.interval < b.interval
      ? -1
      : a.time_range[0].localeCompare(b.time_range[0])
  );
  const config = {
    value_unit,
    interval_type: weekly ? 2 : 1,
    schedules,
  };
  if (weekly) {
    config.start_date = vip_start_date || null;
    config.end_date = vip_end_date || null;
  }
  return config;
};

const buildBatchConfig = ({ batch_values, value_unit }) => {
  const rows = [];
  let defaultValue = null;
  let defaultIntl = null;
  asArray(batch_values).forEach((row) => {
    const value = numOrNull(row?.value);
    if (value === null) return;
    const intl = numOrNull(row?.value_international);
    if (row?.batch_uid === BATCH_FUTURE_UID) {
      defaultValue = value;
      defaultIntl = intl;
    } else if (row?.batch_uid) {
      rows.push({
        batch_uuid: row.batch_uid,
        value,
        ...(intl !== null && { value_international: intl }),
      });
    }
  });
  if (defaultValue === null) return null;
  return {
    value_unit,
    rows,
    default: {
      value: defaultValue,
      ...(defaultIntl !== null && { value_international: defaultIntl }),
    },
  };
};

/** Exported for the international (`value_international`) payload. */
export { buildVipSchedules, buildBatchConfig };

/**
 * Backend `trigger_config` for a form-level string trigger, or null when the
 * trigger is time-based (schedule/evergreen carry no trigger_config).
 */
export const buildBackendTriggerConfig = ({
  trigger,
  tiers,
  threshold_unit,
  value_unit,
  vip_apply_by,
  vip_windows,
  vip_start_date,
  vip_end_date,
  batch_values,
} = {}) => {
  const triggerInt = mapTriggerType(trigger);
  if (TIME_TRIGGER_INTS.includes(triggerInt)) return null;
  if (triggerInt === BACKEND_TRIGGER_TYPE[TRIGGER_TYPE.BATCH_WISE]) {
    return buildBatchConfig({ batch_values, value_unit });
  }
  if (triggerInt === BACKEND_TRIGGER_TYPE[TRIGGER_TYPE.VIP_SLOTS]) {
    return buildVipSchedules({
      vip_apply_by,
      vip_windows,
      vip_start_date,
      vip_end_date,
      value_unit,
    });
  }
  if (triggerInt === 5) {
    return buildTimeTiers({ tiers, value_unit });
  }
  return buildCountTiers({ tiers, threshold_unit, value_unit });
};
